import copy from "clipboard-copy";
import { useCallback, useMemo, useState } from "react";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  findDemoAthleteEntry,
  getDemoEmail,
} from "../../../../utils/user-helpers";
import DemoStylePicker from "./DemoStylePicker";

const DEFAULT_IOS = "https://apps.apple.com/us/app/mystrengthbook/id1337179045";
const DEFAULT_ANDROID =
  "https://play.google.com/store/apps/details?id=com.mystrengthbook&hl=en_US&gl=US&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1";

function storageKeyForCoach(coachId) {
  return `msb_demo_athlete_instructions_dismissed_${coachId}`;
}

/** True when `registered` is missing/invalid or account is not yet 1 calendar month old. */
function isWithinFirstMonthSinceRegistration(registered) {
  if (registered == null) return true;
  const start = new Date(registered);
  if (Number.isNaN(start.getTime())) return true;
  const oneMonthAfter = new Date(start);
  oneMonthAfter.setMonth(oneMonthAfter.getMonth() + 1);
  return Date.now() <= oneMonthAfter.getTime();
}

/**
 * @param {Object} props
 * @param {Object} props.activeUser - Logged-in coach
 * @param {boolean} [props.dismissible] - Athletes list: dismissible; hidden after 1 calendar month from `activeUser.registered` or when dismissed (profile view ignores this)
 * @param {string} [props.visibleForAthleteId] - If set, only show when this athlete is the demo entry
 * @param {string} [props.className] - Extra class on root
 */
export default function DemoAthleteInstructionsCard({
  activeUser,
  dismissible = false,
  visibleForAthleteId = null,
  className = "",
}) {
  const demoEntry = useMemo(
    () => findDemoAthleteEntry(activeUser),
    [activeUser]
  );

  const [dismissed, setDismissed] = useState(() => {
    if (!dismissible || globalThis.window === undefined || !activeUser?._id) {
      return false;
    }
    return (
      globalThis.localStorage.getItem(storageKeyForCoach(activeUser._id)) ===
      "1"
    );
  });

  const demoEmail = demoEntry?.email || getDemoEmail(activeUser.email);
  const appStoreLink = activeUser.appStoreLink || DEFAULT_IOS;
  const googlePlayLink = activeUser.googlePlayLink || DEFAULT_ANDROID;

  const handleDismiss = useCallback(() => {
    if (!activeUser?._id) return;
    try {
      globalThis.localStorage.setItem(storageKeyForCoach(activeUser._id), "1");
    } catch (_) {
      console.log(_);
    }
    setDismissed(true);
  }, [activeUser]);

  const handleCopyEmail = useCallback(() => {
    copy(demoEmail).catch(() => {});
  }, [demoEmail]);

  const isWrongProfile =
    visibleForAthleteId != null &&
    demoEntry &&
    String(demoEntry._id) !== String(visibleForAthleteId);

  if (
    !demoEntry ||
    isWrongProfile ||
    (dismissible &&
      (!isWithinFirstMonthSinceRegistration(activeUser?.registered) ||
        dismissed))
  ) {
    return null;
  }

  const isSeeded = demoEntry?.demoSeeded;

  return (
    <section
      className={`demo-athlete-instructions-card ${className}`.trim()}
      aria-label="Demo athlete mobile login"
    >
      <div className="demo-athlete-instructions-card__header">
        <h2 className="demo-athlete-instructions-card__title">
          {isSeeded
            ? "Try the athlete app (demo account)"
            : "Set up your demo account"}
        </h2>
        {dismissible ? (
          <button
            type="button"
            data-button-type="tertiary"
            data-icon-only="true"
            className="demo-athlete-instructions-card__dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            <SvgIcon
              src="/img/new-icons/red-cross.svg"
              noFilterOnLight={true}
            />
          </button>
        ) : null}
      </div>
      {isSeeded ? (
        <>
          <p className="demo-athlete-instructions-card__lead">
            Log in on iOS or Android as your demo athlete to see training,
            nutrition, and check-ins from their perspective. Use the same
            password as your coach account.
          </p>
          <dl className="demo-athlete-instructions-card__credentials">
            <div>
              <dt>Demo athlete email</dt>
              <dd>
                <code>{demoEmail}</code>
                <button
                  type="button"
                  data-icon-only="true"
                  className="demo-athlete-instructions-card__copy"
                  onClick={handleCopyEmail}
                  aria-label="Copy demo athlete email"
                >
                  <SvgIcon src="/img/new-icons/copy-grey.svg" />
                </button>
              </dd>
            </div>
            <div>
              <dt>Password</dt>
              <dd>Same as your coach account</dd>
            </div>
          </dl>
          <div className="demo-athlete-instructions-card__stores">
            <a
              href={appStoreLink}
              target="_blank"
              rel="noopener noreferrer"
              data-button-type="secondary"
              role="button"
            >
              App Store
            </a>
            <a
              href={googlePlayLink}
              target="_blank"
              rel="noopener noreferrer"
              data-button-type="secondary"
              role="button"
            >
              Google Play
            </a>
          </div>
        </>
      ) : (
        <DemoStylePicker athleteId={demoEntry?._id} />
      )}
    </section>
  );
}
